import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { format, parseISO } from 'date-fns';

import { BoxShadow } from '../../../shared/GlobalStyles';

const LegendContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-bottom: 10px;
`;

const LegendItem = styled('div')`
  align-items: center;
  cursor: ${({ isThreshold }) => (isThreshold ? 'pointer' : 'default')};
  display: flex;
`;

const LegendText = styled('span')`
  color: ${({ isThreshold, isActive }) => (isThreshold && !isActive ? '#9E9E9E' : '#444444')};
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
`;

const TooltipContainer = styled('div')`
  background-color: #FFFFFF;
  border: solid 1px #EEEEEE;
  border-radius: 8px;
  box-shadow: ${BoxShadow};
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  justify-content: flex-start;
  padding: 8px 16px;
  width: 275px;
`;

const TooltipHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TooltipLabel = styled('span')`
  color: ${({ color }) => color};
`;

const TooltipLabelValue = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const TooltipSeparator = styled('div')`
  background-color: #EEEEEE;
  height: 1px;
  margin-bottom: 8px;
  width: 95%;
`;

const TooltipTitle = styled('p')`
  font-weight: 700;
  margin: 0;
`;

const TooltipValue = styled('span')`
  color: #444444;
`;

export const CustomLegend = ({
  payload,
  thresholds,
  thresholdVis,
  onToggle,
}) => {
  const thresholdItems = thresholds.map((t, i) => ({
    color: '#FFCD29',
    dataKey: `threshold_${i}`,
    payloadType: 'threshold',
    type: 'square',
    value: `${t.min} - ${t.max} m/s`,
  }));

  const mergedPayload = [...payload, ...thresholdItems];

  return (
    <LegendContainer>
      {mergedPayload.map((entry) => {
        const isLoad = entry?.dataKey === 'load';
        const isThreshold = entry?.dataKey?.startsWith('threshold_');
        const isVelocity = entry?.dataKey === 'velocity';
        const thresholdIndex = isThreshold
          ? parseInt(entry?.dataKey?.split('_')[1], 10)
          : null;
        const isActive = isThreshold ? thresholdVis?.[thresholdIndex] !== false : true;

        let backgroundColor = 'transparent';
        if (isThreshold) {
          backgroundColor = isActive ? 'rgba(255, 205, 41, 0.2)' : 'rgba(158, 158, 158, 0.2)';
        } else if (isVelocity) {
          backgroundColor = entry.color;
        }

        let borderBottom = '';
        let borderTop = '';
        if (isThreshold && isActive) {
          borderBottom = 'solid 1px #FFCD29';
          borderTop = 'solid 1px #FFCD29';
        } else if (isThreshold && !isActive) {
          borderBottom = 'solid 1px #9E9E9E';
          borderTop = 'solid 1px #9E9E9E';
        }

        const iconStyle = {
          backgroundColor,
          borderBottom,
          borderTop,
          height: isVelocity ? '10.5px' : '14px',
          marginRight: '5px',
          marginTop: isVelocity ? '5px' : '',
          opacity: isThreshold && !isActive ? 0.2 : 1,
          width: '14px',
        };

        // Keyboard accessibility handler
        const handleKeyDown = (e) => {
          if (isThreshold && (e.key === 'Enter' || e.key === ' ')) {
            onToggle(thresholdIndex);
          }
        };

        return (
          <LegendItem
            key={entry.dataKey}
            onClick={() => isThreshold && onToggle(thresholdIndex)}
            onKeyDown={handleKeyDown}
            role={isThreshold ? 'button' : undefined}
            tabIndex={isThreshold ? 0 : undefined}
            isThreshold={isThreshold}
          >
            {isLoad && (
              <svg height='14' style={{ marginRight: '4px', marginTop: '5px' }} width='14'>
                <line
                  stroke={entry.color}
                  strokeWidth='2'
                  x1='0'
                  y1='5'
                  x2='24'
                  y2='5'
                />
              </svg>
            )}

            {isVelocity && <div style={iconStyle} />}

            {isThreshold && <div style={iconStyle} />}

            <LegendText
              isActive={isActive}
              isThreshold={isThreshold}
            >
              {entry.value}
            </LegendText>
          </LegendItem>
        );
      })}
    </LegendContainer>
  );
};

CustomLegend.propTypes = {
  onToggle: PropTypes.func,
  payload: PropTypes.arrayOf(PropTypes.shape({})),
  thresholdVis: PropTypes.shape({}),
  thresholds: PropTypes.arrayOf(PropTypes.shape({})),
};

CustomLegend.defaultProps = {
  onToggle: () => {},
  payload: [],
  thresholdVis: {},
  thresholds: [],
};

export const CustomTooltip = ({
  active, payload, label, weightLabel,
}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }
  const d = payload[0]?.payload;
  if (!d || d.isGap) {
    return null;
  }

  // Format date as "Jun 9, 2025"
  const formatDate = (dateString) => {
    if (!dateString) return label;
    const date = parseISO(dateString);
    return format(date, 'MMM dd, yyyy');
  };

  return (
    <TooltipContainer className='custom-tooltip'>
      <TooltipHeader>
        <TooltipTitle>{formatDate(d.date)}</TooltipTitle>
        <TooltipTitle>
          Set
          {' '}
          {d.setNumber}
          {' '}
          of
          {' '}
          {d.setLength}
        </TooltipTitle>
      </TooltipHeader>
      <TooltipSeparator />
      <TooltipLabelValue>
        <TooltipLabel color='#FF6600'>Load</TooltipLabel>
        <TooltipValue>
          {d.load}
          {' '}
          {weightLabel}
        </TooltipValue>
      </TooltipLabelValue>
      <TooltipLabelValue>
        <TooltipLabel color='#7B61FF'>Set Avg Velocity</TooltipLabel>
        <TooltipValue>
          {d.meanVelocity}
          {' '}
          m/s
        </TooltipValue>
      </TooltipLabelValue>
    </TooltipContainer>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.string,
  payload: PropTypes.arrayOf(PropTypes.shape({
    payload: PropTypes.shape({}),
  })),
  weightLabel: PropTypes.string,
};

CustomTooltip.defaultProps = {
  active: false,
  label: '',
  payload: [],
  weightLabel: 'lbs',
};
